import React, { useState } from 'react';
import { theme } from '../theme';

// ---------------------------------------------------------------------------
// Reusable components
// ---------------------------------------------------------------------------
export function Button({ children, variant = 'primary', size = 'md', icon, disabled, onClick, style }) {
  const variants = {
    primary: { bg: theme.accent, bgHover: theme.accentHover, color: '#fff' },
    danger: { bg: theme.danger, bgHover: theme.dangerHover, color: '#fff' },
    ghost: { bg: 'transparent', bgHover: theme.surfaceHover, color: theme.textMuted },
    outline: { bg: 'transparent', bgHover: theme.surfaceHover, color: theme.text, border: theme.border },
    success: { bg: theme.success, bgHover: '#16a34a', color: '#fff' },
  };
  const sizes = { sm: '5px 10px', md: '8px 16px', lg: '10px 20px' };
  const v = variants[variant] || variants.primary;
  const [hovered, setHovered] = useState(false);
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        padding: sizes[size], fontSize: size === 'sm' ? '12px' : '13px',
        border: v.border ? `1px solid ${v.border}` : 'none',
        borderRadius: '6px', cursor: disabled ? 'not-allowed' : 'pointer',
        background: disabled ? theme.border : hovered ? v.bgHover : v.bg,
        color: disabled ? theme.textMuted : v.color,
        transition: 'all 0.15s', fontWeight: 500, whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {icon}
      {children}
    </button>
  );
}

export function Input({ label, value, onChange, placeholder, type = 'text', style, autoComplete, selectOnFocus }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {label && <label style={{ fontSize: '12px', color: theme.textMuted, fontWeight: 500 }}>{label}</label>}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        style={{
          padding: '8px 12px', background: theme.codeBg, border: `1px solid ${theme.border}`,
          borderRadius: '6px', color: theme.text, fontSize: '13px', outline: 'none',
          transition: 'border 0.15s', ...style,
        }}
        onFocus={(e) => {
          e.target.style.borderColor = theme.borderFocus;
          if (selectOnFocus) e.target.select();
        }}
        onBlur={(e) => (e.target.style.borderColor = theme.border)}
      />
    </div>
  );
}

export function Badge({ children, color = theme.accent }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', fontSize: '11px', fontWeight: 600,
      borderRadius: '10px', background: `${color}22`, color: color,
    }}>
      {children}
    </span>
  );
}

export function Spinner() {
  return (
    <div style={{
      display: 'inline-block', width: '20px', height: '20px',
      border: `2px solid ${theme.border}`, borderTopColor: theme.accent,
      borderRadius: '50%', animation: 'spin 0.8s linear infinite',
    }} />
  );
}

// ---------------------------------------------------------------------------
// Sidebar nav item
// ---------------------------------------------------------------------------
export function SidebarItem({ icon, label, active, onClick, badge }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
        padding: '9px 14px', border: 'none', borderRadius: '8px', cursor: 'pointer',
        background: active ? `${theme.accent}18` : hovered ? theme.surfaceHover : 'transparent',
        color: active ? theme.accent : theme.textMuted, fontSize: '13px', fontWeight: 500,
        transition: 'all 0.15s', textAlign: 'left',
        borderLeft: active ? `2px solid ${theme.accent}` : '2px solid transparent',
      }}
    >
      {icon}
      <span style={{ flex: 1 }}>{label}</span>
      {badge != null && badge > 0 && (
        <span style={{
          fontSize: '11px', background: `${theme.accent}22`, color: theme.accent,
          padding: '1px 7px', borderRadius: '10px', fontWeight: 600,
        }}>{badge}</span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Select dropdown (styled)
// ---------------------------------------------------------------------------
export function Select({ value, onChange, options, style }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        padding: '5px 8px', background: theme.codeBg, border: `1px solid ${theme.border}`,
        borderRadius: '5px', color: theme.text, fontSize: '12px', outline: 'none',
        cursor: 'pointer', appearance: 'auto', ...style,
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}
